import { FC } from 'react';
import InputControl from '@/components/form/input-control';
import { Box, Grid } from '@mui/material';
import FormSection from '@/components/form/form-section';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { useQuery } from 'react-query';
import useQueryHelper from '@/hooks/use-query-helper';
import { getScopedPolicyWithParents, patchPolicy } from '@/lib/services/policy.service';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import useToast from '@/hooks/use-toast.hook';
import FormButtons from '@/components/form/form-buttons';
import FormLabel from '@/components/form-label';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import ContentControls from '@/components/form/content-controls';
import { FieldError } from '@/lib/services';
import MonitorWarningToast from './monitor-warning-toast';
import { OnOffToggleButtonValue } from '@/components/form/on-off-toggle-button/helpers';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const formModel = new PolicyModel();
const fields = ['sensitiveData', 'sensitiveDataSources', 'contentControls'];

interface Props {
  policyId: string;
}

const GroupPolicySensitiveDataForm: FC<Props> = ({ policyId }) => {
  const { toast, errorToast } = useToast();
  const { canChangePolicy } = useFeature();
  const { canViewConfidentialDatasources } = useFeature();

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const { patched, fieldErrors, error } = await patchPolicy(
        values.id,
        values.propSlice(fields)
      );
      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        toast('The policy was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const { values, setFieldValue } = formHandle;

  const query = useQuery([QueryKey.PolicyCompView, policyId], async () =>
    getScopedPolicyWithParents(policyId)
  );
  const { showLoader } = useQueryHelper(query);
  const { data } = query;
  const [policy, defaultPolicy] = data || [null, new PolicyModel()];
  const { isDefault: isDefaultPolicy } = policy || { isDefault: false };

  const handleChangeDetect = (value: OnOffToggleButtonValue) => {
    if (value === undefined) {
      setFieldValue('sensitiveData.action', undefined);
    }
  };

  if (showLoader) {
    return null;
  }

  const detectDisabled = !values.sensitiveData?.detect;

  return (
    <Box>
      <Form formHandle={formHandle} query={query}>
        <PageFeatureToast featureId="change-policy" can={canChangePolicy} />

        <MonitorWarningToast policy={policy!} />

        <ContentControls
          formHandle={formHandle}
          readonly={!canChangePolicy}
          policy={policy!}
          defaultPolicy={isDefaultPolicy ? undefined : defaultPolicy}
          withNotSet={!isDefaultPolicy}
        />

        <FormSection title="PII Detection Settings" scrollId="detection-settings">
          <FormSectionSubtitle>
            Enable or disable detection of PII entities in requests to public services, and choose
            which action to take when they are detected.
          </FormSectionSubtitle>
          <Grid container alignItems="top">
            <Grid item xs={5}>
              <FormLabel>Detection</FormLabel>
              <InputControl
                name="sensitiveData.detect"
                type="on-off-toggle-button"
                formHandle={formHandle}
                readonly={!canChangePolicy}
                onChange={handleChangeDetect}
                withNotSet={!isDefaultPolicy}
              />
            </Grid>
            <Grid item xs={1}></Grid>
            <Grid item xs={5}>
              <FormLabel>Action</FormLabel>
              <InputControl
                name="sensitiveData.action"
                type="sd-action-select"
                formHandle={formHandle}
                disabled={detectDisabled}
                readonly={!canChangePolicy}
                defaultPolicy={isDefaultPolicy ? undefined : defaultPolicy}
                withNotSet={!isDefaultPolicy}
              />
            </Grid>
          </Grid>
        </FormSection>

        <FormSection
          title="PII Entities"
          locked={detectDisabled}
          scrollId="pii-entities"
          tooltip="Entities cannot be configured because PII detection is disabled above."
        >
          <FormSectionSubtitle>
            Choose which PII entities to detect, and optionally configure detection mode.
          </FormSectionSubtitle>
          <Grid container alignItems="top">
            <Grid item xs={12} mb={2}>
              <InputControl
                name="sensitiveData.entities"
                type="sd-entity-list"
                formHandle={formHandle}
                disabled={detectDisabled}
                readonly={!canChangePolicy}
                height={400}
                policy={policy!}
              />
            </Grid>
          </Grid>
        </FormSection>

        {canViewConfidentialDatasources && (
          <FormSection title="Confidential Data Sources" scrollId="confidential-data-sources">
            <FormSectionSubtitle>
              Choose which data sources contain confidential data. If a request matches content in
              an assigned data source, take the action specified for the confidential data content
              control.
            </FormSectionSubtitle>
            <InputControl
              name="sensitiveDataSources"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="datasources-list-toggle"
              disabled={detectDisabled}
            />
          </FormSection>
        )}

        <FormButtons formHandle={formHandle} readonly={!canChangePolicy} />
      </Form>
    </Box>
  );
};

export default GroupPolicySensitiveDataForm;
